import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Divider,
  Stack,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  FormControl,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import {
  useTrainerInstitutes,
  useTrainerDomains,
  useTrainerCalendarAvailability,
  useBookTrainerSlot,
} from "../../../hooks/useTrainers";

const MAX_IMAGE_SIZE = 500 * 1024; // 500 KB

// Helper to format Date to YYYY-MM-DD
const formatDateToYYYYMMDD = (d) => {
  if (!d || isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Calculate current week's Monday (fallback)
const getThisWeekMonday = () => {
  const today = new Date();
  const day = today.getDay(); // 0 is Sun, 1 is Mon...
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

// Calculate 6 months from today for maximum date limit
const getMaxDate6Months = () => {
  const max = new Date();
  max.setMonth(max.getMonth() + 6);
  max.setHours(23, 59, 59, 999);
  return max;
};

// Check if all consecutive days in a date window are free (not in unavailable dates set)
const isDateRangeAvailable = (startDate, numDays, unavailableSet) => {
  const cur = new Date(startDate);
  cur.setHours(0, 0, 0, 0);
  for (let i = 0; i < numDays; i++) {
    const dStr = formatDateToYYYYMMDD(cur);
    if (unavailableSet.has(dStr)) {
      return false;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return true;
};

const INITIAL_COORDINATOR = {
  name: "",
  mobile: "",
  email: "",
  gender: "Male",
};

const INITIAL_COORDINATOR_ERROR = {
  name: "",
  mobile: "",
  email: "",
  gender: "",
};

const INITIAL_FORM = {
  institute_id: "",
  domain_id: "",
  startDate: null,
  endDate: null,
  // edition_no: "",
  total_faculty_limit: "",
  students_limit: "",
  coordinators: [{ ...INITIAL_COORDINATOR }],
};

const INITIAL_ERRORS = {
  institute_id: "",
  domain_id: "",
  start_date: "",
  end_date: "",
  // edition_no: "",
  total_faculty_limit: "",
  students_limit: "",
  coordinators: [{ ...INITIAL_COORDINATOR_ERROR }],
  image: "",
};

const validateEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "" : "Enter a valid email address.";

const validateMobile = (v) =>
  /^[6-9]\d{9}$/.test(v) ? "" : "Enter a valid 10-digit mobile number.";

export default function InstituteBookingModal({
  open,
  onClose,
  institutes = [],
  onSuccess,
}) {
  const [eventType, setEventType] = useState("fdp"); // "fdp" or "tech_camp"
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [bookError, setBookError] = useState("");
  const imageInputRef = useRef(null);

  // React Query mutation for booking a slot
  const { mutateAsync: bookSlotMutation, isLoading: bookLoading } = useBookTrainerSlot();


  // Fetch institutes assigned to trainer for the dropdown
  const { data: trainerInstitutes = [], isLoading: loadingInstitutes } = useTrainerInstitutes({
    enabled: open,
  });

  // Fetch domains assigned to trainer for the dropdown based on eventType
  const { data: trainerDomains = [], isLoading: loadingDomains } = useTrainerDomains(
    { event_type: eventType },
    { enabled: open && Boolean(eventType) }
  );

  // Fetch calendar availability from backend for the selected institute, domain, and eventType
  const {
    data: calendarAvailability,
    isLoading: loadingCalendar,
  } = useTrainerCalendarAvailability(
    {
      institute_id: form.institute_id,
      domain_id: form.domain_id,
      event_type: eventType,
    },
    {
      enabled: open && Boolean(form.institute_id && form.domain_id && eventType),
    }
  );

  const availableInstitutes = useMemo(() => {
    if (Array.isArray(trainerInstitutes) && trainerInstitutes.length > 0) {
      return trainerInstitutes;
    }
    return Array.isArray(institutes) ? institutes : [];
  }, [trainerInstitutes, institutes]);

  const isFdp = eventType === "fdp";
  const isTechCamp = eventType === "tech_camp";

  // Build set of unavailable / blocked / busy dates returned by backend
  const unavailableDatesSet = useMemo(() => {
    const set = new Set();
    if (calendarAvailability && Array.isArray(calendarAvailability.dates)) {
      calendarAvailability.dates.forEach((item) => {
        if (item.date) {
          // If status is present or available_trainers === 0, the date is blocked/busy
          if (item.status !== undefined || item.available_trainers === 0) {
            set.add(item.date);
          }
        }
      });
    }
    return set;
  }, [calendarAvailability]);

  // First selectable date from backend or fallback to this week's Monday
  const firstSelectableDate = useMemo(() => {
    if (calendarAvailability?.first_selectable_date) {
      const parts = calendarAvailability.first_selectable_date.split("-");
      if (parts.length === 3) {
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 0, 0, 0, 0);
        if (!isNaN(d.getTime())) return d;
      }
    }
    return getThisWeekMonday();
  }, [calendarAvailability]);

  // Maximum date limit (6 months from today)
  const maxDate = useMemo(() => getMaxDate6Months(), []);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({
        ...INITIAL_FORM,
        coordinators: [{ ...INITIAL_COORDINATOR }],
      });
      setErrors({
        ...INITIAL_ERRORS,
        coordinators: [{ ...INITIAL_COORDINATOR_ERROR }],
      });
      setImageFile(null);
      setImagePreview(null);
      setBookError("");
    }
  }, [open]);

  // Auto-select single institute if only 1 is available
  useEffect(() => {
    if (open && availableInstitutes.length === 1) {
      setForm((prev) => ({
        ...prev,
        institute_id: String(availableInstitutes[0].institute_id),
      }));
    }
  }, [open, availableInstitutes]);

  // Auto-select single domain or ensure selected domain exists in updated list
  useEffect(() => {
    if (open && Array.isArray(trainerDomains)) {
      setForm((prev) => {
        if (trainerDomains.length === 1) {
          return { ...prev, domain_id: String(trainerDomains[0].domain_id) };
        }
        if (prev.domain_id && !trainerDomains.some((d) => String(d.domain_id) === String(prev.domain_id))) {
          return { ...prev, domain_id: "" };
        }
        return prev;
      });
    }
  }, [open, trainerDomains]);

  // When event type switches, reset domain and dates
  const handleEventTypeChange = (newType) => {
    setEventType(newType);
    setForm((prev) => ({ ...prev, domain_id: "", startDate: null, endDate: null, students_limit: "", total_faculty_limit: "" }));
    setErrors((prev) => ({ ...prev, domain_id: "", start_date: "", end_date: "", students_limit: "", total_faculty_limit: "" }));
  };

  // DatePicker disable condition for Start Date based on backend availability
  const shouldDisableStartDate = (date) => {
    if (!date || isNaN(date.getTime())) return true;
    if (!form.institute_id || !form.domain_id) return true;
    if (loadingCalendar) return true;

    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    // Disable if before first selectable date from backend
    if (d < firstSelectableDate) {
      return true;
    }

    // Disable if past max date
    if (d > maxDate) {
      return true;
    }

    const dayOfWeek = d.getDay(); // 0 is Sun, 1 is Mon, 4 is Thu

    if (isFdp) {
      // FDP must start on Monday and have all 6 days free (Mon-Sat)
      if (dayOfWeek !== 1) return true;
      return !isDateRangeAvailable(d, 6, unavailableDatesSet);
    } else {
      // Tech Camp must start on Monday or Thursday and have all 3 days free
      if (dayOfWeek !== 1 && dayOfWeek !== 4) return true;
      return !isDateRangeAvailable(d, 3, unavailableDatesSet);
    }
  };

  // DatePicker disable condition for End Date (only allows the acceptable completion date for the chosen start date)
  const shouldDisableEndDate = (date) => {
    if (!form.startDate || !date) return true;

    const start = new Date(form.startDate);
    start.setHours(0, 0, 0, 0);

    const target = new Date(date);
    target.setHours(0, 0, 0, 0);

    if (isFdp) {
      // For FDP: Saturday (+5 days from start Monday) is allowed (Mon–Sat)
      const saturday = new Date(start);
      saturday.setDate(saturday.getDate() + 5);
      return target.getTime() !== saturday.getTime();
    } else {
      // For Tech Camp
      const startDay = start.getDay();
      if (startDay === 1 || startDay === 4) {
        const end = new Date(start);
        end.setDate(end.getDate() + 2);
        return target.getTime() !== end.getTime();
      }
    }
    return false;
  };

  const handleStartDatePickerChange = (newDate) => {
    if (!newDate || isNaN(newDate.getTime())) {
      setForm((prev) => ({ ...prev, startDate: null, endDate: null }));
      setErrors((prev) => ({ ...prev, start_date: "Start date is required." }));
      return;
    }

    const dayOfWeek = newDate.getDay();
    let calculatedEndDate = null;

    if (isFdp) {
      if (dayOfWeek === 1) {
        // Monday -> Saturday (+5 days: 6 consecutive working days)
        calculatedEndDate = new Date(newDate);
        calculatedEndDate.setDate(calculatedEndDate.getDate() + 5);
      }
    } else {
      if (dayOfWeek === 1 || dayOfWeek === 4) {
        // Monday -> Wednesday (+2 days) or Thursday -> Saturday (+2 days)
        calculatedEndDate = new Date(newDate);
        calculatedEndDate.setDate(calculatedEndDate.getDate() + 2);
      }
    }

    setForm((prev) => ({
      ...prev,
      startDate: newDate,
      endDate: calculatedEndDate,
    }));

    setErrors((prev) => ({
      ...prev,
      start_date: "",
      end_date: "",
    }));
  };

  const validateField = (name, value) => {
    switch (name) {
      case "institute_id":
        return !value ? "Institute is required." : "";
      case "domain_id":
        return !value ? "Domain is required." : "";
      // case "edition_no":
      //   return !value || Number(value) < 1
      //     ? "Edition number is required and must be ≥ 1."
      //     : "";
      case "total_faculty_limit":
        if (isFdp)
          return !value || Number(value) < 1
            ? "Total faculty limit is required and must be ≥ 1."
            : "";
        return "";
      case "students_limit":
        if (isTechCamp)
          return !value || Number(value) < 1
            ? "Student limit is required and must be ≥ 1."
            : "";
        return "";
      default:
        return "";
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "institute_id" || name === "domain_id") {
      setForm((prev) => ({ ...prev, [name]: value, startDate: null, endDate: null }));
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
        start_date: "",
        end_date: "",
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleCoordinatorChange = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.coordinators];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, coordinators: updated };
    });

    setErrors((prev) => {
      const updatedErrors = [...(prev.coordinators || [])];
      let fieldErr = "";
      if (field === "name") {
        fieldErr = !value?.trim() ? "Coordinator name is required." : "";
      } else if (field === "mobile") {
        fieldErr = validateMobile(value);
      } else if (field === "email") {
        fieldErr = validateEmail(value);
      } else if (field === "gender") {
        fieldErr = !value ? "Please select a gender." : "";
      }
      updatedErrors[index] = {
        ...(updatedErrors[index] || INITIAL_COORDINATOR_ERROR),
        [field]: fieldErr,
      };
      return { ...prev, coordinators: updatedErrors };
    });
  };

  const handleAddCoordinator = () => {
    if (form.coordinators.length >= 3) return;
    setForm((prev) => ({
      ...prev,
      coordinators: [...prev.coordinators, { ...INITIAL_COORDINATOR }],
    }));
    setErrors((prev) => ({
      ...prev,
      coordinators: [
        ...(prev.coordinators || []),
        { ...INITIAL_COORDINATOR_ERROR },
      ],
    }));
  };

  const handleRemoveCoordinator = (indexToRemove) => {
    if (form.coordinators.length <= 1) return;
    setForm((prev) => ({
      ...prev,
      coordinators: prev.coordinators.filter((_, idx) => idx !== indexToRemove),
    }));
    setErrors((prev) => ({
      ...prev,
      coordinators: (prev.coordinators || []).filter(
        (_, idx) => idx !== indexToRemove
      ),
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        image: "Image must be less than 500 KB.",
      }));
      setImageFile(null);
      setImagePreview(null);
      e.target.value = "";
      return;
    }
    setErrors((prev) => ({ ...prev, image: "" }));
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validateAll = () => {
    const fields = [
      "institute_id",
      "domain_id",
      // "edition_no",
      ...(isFdp ? ["total_faculty_limit"] : []),
      ...(isTechCamp ? ["students_limit"] : []),
    ];

    const newErrors = { ...INITIAL_ERRORS };
    let valid = true;

    fields.forEach((name) => {
      const err = validateField(name, form[name]);
      newErrors[name] = err;
      if (err) valid = false;
    });

    const coordsErrors = form.coordinators.map((c) => {
      const cErr = {
        name: !c.name?.trim() ? "Coordinator name is required." : "",
        mobile: validateMobile(c.mobile),
        email: validateEmail(c.email),
        gender: !c.gender ? "Please select a gender." : "",
      };
      if (cErr.name || cErr.mobile || cErr.email || cErr.gender) {
        valid = false;
      }
      return cErr;
    });
    newErrors.coordinators = coordsErrors;

    if (!form.startDate || isNaN(form.startDate.getTime())) {
      newErrors.start_date = "Start date is required.";
      valid = false;
    }
    if (!form.endDate || isNaN(form.endDate.getTime())) {
      newErrors.end_date = "End date is required.";
      valid = false;
    }

    if (!imageFile) {
      newErrors.image = "Please upload the hosted institute logo.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleBookSubmit = async () => {
    setBookError("");
    if (!validateAll()) return;

    // Generate dates list (6 for FDP, 3 for TechCamp)
    const datesList = [];
    const numDays = isFdp ? 6 : 3;
    for (let i = 0; i < numDays; i++) {
      const cur = new Date(form.startDate);
      cur.setDate(cur.getDate() + i);
      datesList.push(formatDateToYYYYMMDD(cur));
    }

    const fd = new FormData();
    fd.append("institute_id", form.institute_id);
    fd.append("domain_id", form.domain_id);
    fd.append("dates", JSON.stringify(datesList));
    // if (form.edition_no) {
    //   fd.append("edition_no", form.edition_no);
    // }
    if (isFdp && form.total_faculty_limit) {
      fd.append("total_faculty_limit", form.total_faculty_limit);
    }
    if (isTechCamp && form.students_limit) {
      fd.append("students_limit", form.students_limit);
    }

    const coordinatorsPayload = form.coordinators.map((c) => ({
      name: c.name.trim(),
      gender: c.gender,
      email: c.email.trim(),
      mobile: c.mobile.trim(),
    }));
    fd.append("coordinators", JSON.stringify(coordinatorsPayload));

    if (imageFile) {
      fd.append("image", imageFile);
    }

    // Log form data entries for verification
    console.log("=== Book Slot FormData ===");
    for (let [key, value] of fd.entries()) {
      console.log(`${key}:`, value);
    }

    try {
      const res = await bookSlotMutation(fd);
      const successMsg = res?.message || "Slot booked successfully!";
      onClose();
      if (onSuccess) onSuccess(successMsg);
    } catch (e) {
      setBookError(
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        "Booking failed. Please check the entered details and try again."
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          fontWeight={700}
          sx={{
            pb: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            Book Slot
            <Chip
              label={isFdp ? "FDP" : "TECH CAMP"}
              size="small"
              color={isFdp ? "primary" : "secondary"}
              sx={{ ml: 1.5, fontWeight: 700, fontSize: "0.7rem" }}
            />
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#94A3B8" }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ width: "100%" }}>
            {/* ── 1. Event Type Selector ── */}
            <FormControl component="fieldset">
              <FormLabel
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                Event Type *
              </FormLabel>
              <RadioGroup
                row
                name="event_type"
                value={eventType}
                onChange={(e) => handleEventTypeChange(e.target.value)}
              >
                <FormControlLabel
                  value="fdp"
                  control={<Radio size="small" />}
                  label="FDP"
                />
                <FormControlLabel
                  value="tech_camp"
                  control={<Radio size="small" />}
                  label="Tech Camp"
                />
              </RadioGroup>
            </FormControl>

            {/* ── 2. Institute Name Dropdown ── */}
            <TextField
              select
              label="Institute Name *"
              name="institute_id"
              value={form.institute_id}
              onChange={handleFormChange}
              error={!!errors.institute_id}
              helperText={errors.institute_id}
              size="small"
              fullWidth
              disabled={loadingInstitutes}
            >
              <MenuItem value="">
                {loadingInstitutes ? "Loading institutes..." : "Select Institute"}
              </MenuItem>
              {availableInstitutes.map((inst) => (
                <MenuItem
                  key={inst.institute_id}
                  value={String(inst.institute_id)}
                >
                  {inst.institute_name} {inst.institute_type ? `(${inst.institute_type})` : ""}
                </MenuItem>
              ))}
            </TextField>

            {/* ── 2b. Domain Name Dropdown ── */}
            <TextField
              select
              label="Domain *"
              name="domain_id"
              value={form.domain_id}
              onChange={handleFormChange}
              error={!!errors.domain_id}
              helperText={errors.domain_id}
              size="small"
              fullWidth
              disabled={loadingDomains}
            >
              <MenuItem value="">
                {loadingDomains ? "Loading domains..." : "Select Domain"}
              </MenuItem>
              {trainerDomains.map((domain) => (
                <MenuItem
                  key={domain.domain_id}
                  value={String(domain.domain_id)}
                >
                  {domain.domain_name}
                </MenuItem>
              ))}
            </TextField>

            {/* ── 3. Start Date & End Date Pickers ── */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                width: "100%",
              }}
            >
              <DatePicker
                label="Start Date *"
                value={form.startDate}
                onChange={handleStartDatePickerChange}
                inputFormat="dd/MM/yyyy"
                minDate={firstSelectableDate}
                maxDate={maxDate}
                disabled={!form.institute_id || !form.domain_id || loadingCalendar}
                shouldDisableDate={shouldDisableStartDate}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    error={!!errors.start_date}
                    helperText={
                      errors.start_date ||
                      (!form.institute_id || !form.domain_id
                        ? "Select institute & domain first"
                        : loadingCalendar
                          ? "Checking date availability..."
                          : isFdp
                            ? "Select an available Monday (6-day slot: Mon–Sat)"
                            : "Select an available Mon or Thu (3-day slot)")
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCalendar && (
                            <CircularProgress size={16} sx={{ mr: 1, color: "primary.main" }} />
                          )}
                          {params.InputProps?.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <DatePicker
                label="End Date *"
                value={form.endDate}
                onChange={(newEndDate) => {
                  setForm((prev) => ({ ...prev, endDate: newEndDate }));
                }}
                inputFormat="dd/MM/yyyy"
                minDate={form.startDate || firstSelectableDate}
                maxDate={maxDate}
                disabled={!form.startDate}
                shouldDisableDate={shouldDisableEndDate}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    fullWidth
                    error={!!errors.end_date}
                    helperText={errors.end_date || (form.startDate ? "Auto-calculated completion date" : "")}
                  />
                )}
              />
            </Box>


            {/* ── 4. Edition Number (both FDP & Tech Camp) ── */}
            {/* <TextField
              label="Edition No *"
              name="edition_no"
              type="number"
              size="small"
              fullWidth
              value={form.edition_no}
              onChange={handleFormChange}
              error={!!errors.edition_no}
              helperText={errors.edition_no}
              inputProps={{ min: 1 }}
            /> */}

            {/* ── 4b. Total Faculty Limit (FDP only) ── */}
            {isFdp && (
              <TextField
                label="Total Faculty Limit *"
                name="total_faculty_limit"
                type="number"
                size="small"
                fullWidth
                value={form.total_faculty_limit}
                onChange={handleFormChange}
                error={!!errors.total_faculty_limit}
                helperText={errors.total_faculty_limit}
                inputProps={{ min: 1 }}
              />
            )}

            {/* ── 4c. Student Limit (Tech Camp only) ── */}
            {isTechCamp && (
              <TextField
                label="Student Limit *"
                name="students_limit"
                type="number"
                size="small"
                fullWidth
                value={form.students_limit}
                onChange={handleFormChange}
                error={!!errors.students_limit}
                helperText={errors.students_limit}
                inputProps={{ min: 1 }}
              />
            )}

            {/* ── 5. Coordinator Details Divider ── */}
            <Divider>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Coordinator Details
              </Typography>
            </Divider>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Coordinators ({form.coordinators.length}/3)
              </Typography>
              {form.coordinators.length < 3 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddCoordinator}
                  sx={{
                    textTransform: "none",
                    borderRadius: 1.5,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    py: 0.5,
                    px: 1.5,
                  }}
                >
                  Add Coordinator
                </Button>
              )}
            </Box>

            {/* ── Coordinator Cards ── */}
            {form.coordinators.map((coord, idx) => {
              const coordError = errors.coordinators?.[idx] || {};
              return (
                <Box
                  key={idx}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: idx === 0 ? "rgba(79, 70, 229, 0.2)" : "divider",
                    bgcolor: idx === 0 ? "rgba(79, 70, 229, 0.02)" : "#f8fafc",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.75,
                  }}
                >
                  {form.coordinators.length > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                      >
                        Coordinator {idx + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveCoordinator(idx)}
                        title="Remove Coordinator"
                        sx={{ p: 0.5 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}

                  {/* Name */}
                  <TextField
                    label={`Coordinator ${idx + 1} Name *`}
                    size="small"
                    fullWidth
                    value={coord.name}
                    onChange={(e) =>
                      handleCoordinatorChange(idx, "name", e.target.value)
                    }
                    error={!!coordError.name}
                    helperText={coordError.name}
                  />

                  {/* Mobile & Email in responsive row */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <TextField
                      label="Mobile Number *"
                      size="small"
                      fullWidth
                      value={coord.mobile}
                      onChange={(e) =>
                        handleCoordinatorChange(idx, "mobile", e.target.value)
                      }
                      error={!!coordError.mobile}
                      helperText={coordError.mobile}
                      inputProps={{ maxLength: 10 }}
                    />
                    <TextField
                      label="Email *"
                      type="email"
                      size="small"
                      fullWidth
                      value={coord.email}
                      onChange={(e) =>
                        handleCoordinatorChange(idx, "email", e.target.value)
                      }
                      error={!!coordError.email}
                      helperText={coordError.email}
                    />
                  </Box>

                  {/* Gender */}
                  <FormControl error={!!coordError.gender}>
                    <FormLabel
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: "text.primary",
                        mb: 0.25,
                      }}
                    >
                      Gender *
                    </FormLabel>
                    <RadioGroup
                      row
                      value={coord.gender}
                      onChange={(e) =>
                        handleCoordinatorChange(idx, "gender", e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="Male"
                        control={<Radio size="small" />}
                        label="Male"
                      />
                      <FormControlLabel
                        value="Female"
                        control={<Radio size="small" />}
                        label="Female"
                      />
                      <FormControlLabel
                        value="Other"
                        control={<Radio size="small" />}
                        label="Other"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              );
            })}

            {/* ── 12. Hosted Institute Logo Divider ── */}
            <Divider>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Hosted Institute Logo
              </Typography>
            </Divider>

            {/* ── 13. Image Upload ── */}
            <Box>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileOutlinedIcon />}
                onClick={() => imageInputRef.current.click()}
                fullWidth
                color={errors.image ? "error" : "primary"}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  justifyContent: "flex-start",
                  px: 2,
                }}
              >
                {imageFile
                  ? imageFile.name
                  : "Upload Hosted Institute Logo *"}
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Accepted formats: JPG, PNG, WEBP &nbsp;|&nbsp; Max size: 500 KB
              </Typography>
              {errors.image && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.image}
                </Typography>
              )}

              {/* Image Preview */}
              {imagePreview && (
                <Box
                  mt={1.5}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    overflow: "hidden",
                    width: "100%",
                    maxHeight: 140,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f8fafc",
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxHeight: 140,
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* ── Error Alert ── */}
            {bookError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {bookError}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={bookLoading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBookSubmit}
            disabled={bookLoading}
            startIcon={
              bookLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <BookmarkAddOutlinedIcon />
              )
            }
            sx={{ textTransform: "none" }}
          >
            {bookLoading ? "Booking..." : "Book Slot"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
